import React, { useMemo } from 'react'
import { faBooks } from '@fortawesome/pro-solid-svg-icons'
import { RankmiTable } from '@rankmi/rankmi-table'

import {
  useTranslationRankmi,
  joinKeys,
  generateTableLocale,
} from 'helpers/translations.helper'
import { EnrolExternalCourse } from 'graphqlAPI'
import { canSkipOnRowSelected } from 'helpers'
import { SortValue } from 'models/rankmiTable.model'
import { prefixTranslation } from '../../common'
import { useColumns } from './hooks/useColumns'
import styles from './index.module.scss'

type Id = EnrolExternalCourse['id']

type DataColumn = EnrolExternalCourse & {
  key: EnrolExternalCourse['id']
  action: Id
}

interface ITable {
  data: EnrolExternalCourse[]
  setPage(page: number): void
  setSearch(text: string): void
  setSort: (value: SortValue) => void
  setPerPage(perPage: number): void
  setSelectedRows(ids: Id[]): void
  selectedRows: Id[]
  setAllSelected(state: boolean): void
  total: number
  loading: boolean
}

export const Table: React.FC<ITable> = ({
  data,
  setPage,
  setSearch,
  setSort,
  setPerPage,
  setSelectedRows,
  selectedRows,
  setAllSelected,
  total,
  loading,
}) => {
  const tRelative = useTranslationRankmi({
    prefix: joinKeys(prefixTranslation, 'statistics.table'),
  })

  const translations = useMemo(
    () =>
      generateTableLocale({
        header: {
          rowsCount: tRelative('rowsCount'),
          rowsCount_plural: tRelative('rowsCount_plural'),
        },
      }),
    [tRelative]
  )

  const dataSource = useMemo<DataColumn[]>(() => {
    return data.map<DataColumn>((row) => {
      const { id } = row
      return {
        ...row,
        key: id,
        action: id,
      }
    })
  }, [data])

  const columns = useColumns()

  return (
    <div className={styles.container}>
      <RankmiTable
        columnHeaders={columns}
        data={dataSource}
        selectedRows={selectedRows}
        total={total}
        onPaginationChange={(page: any) => setPage(page)}
        onSearch={(text: string) => setSearch(text)}
        onSortChange={(sortData: any) => setSort(sortData)}
        loading={loading}
        onRowsPerPageChange={setPerPage}
        onRowSelected={({
          selectedRowKeys,
          allSelected,
          unselectedRowKeys,
          dispatcher,
        }: any) => {
          if (canSkipOnRowSelected(dispatcher)) return
          setSelectedRows(allSelected ? unselectedRowKeys : selectedRowKeys)
          setAllSelected(allSelected)
        }}
        locale={translations}
        countIcon={faBooks}
        initialRowsPerPage={10}
        simpleSelectAll
        responsive
      />
    </div>
  )
}

export default Table
